import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOSchema from '@/components/base/SEOSchema';
import MovieCard from '@/components/feature/MovieCard';
import { allContent, categories } from '@/mocks/content';

const faqData = [
  {
    question: '色影视网站上的视频需要付费吗？',
    answer: '不需要！色影视是一个免费的在线影视平台，所有电视剧全集、电影完整版、综艺全集均可免费观看，无需任何付费或会员订阅。',
  },
  {
    question: '为什么色影视播放视频不会缓冲卡顿？',
    answer: '色影视采用了先进的视频CDN加速技术和自适应码率播放方案，能够根据您的网络状况自动调整画质，确保不缓冲追剧的流畅体验。同时我们优化的服务器架构保障了高峰时段的稳定播放。',
  },
  {
    question: '色影视支持哪些设备观看？',
    answer: '色影视全面支持电脑、平板、手机等多种设备。无论是在家使用大屏电脑观看电影完整版，还是通勤路上用手机追电视剧全集，都能获得高清流畅的观看体验。',
  },
  {
    question: '色影视的内容更新频率如何？',
    answer: '色影视每日更新最新影视剧集，热门电视剧全集上线后第一时间同步更新。综艺节目通常在播出后24小时内即可观看全集播放，确保您不会错过任何精彩内容。',
  },
  {
    question: '色影视的电影和电视剧画质如何？',
    answer: '色影视提供高清画质播放，大部分内容支持1080P全高清甚至4K超高清画质。我们致力于为用户提供影院级的观影体验，让每一帧画面都清晰细腻。',
  },
  {
    question: '如何在色影视上找到想看的影视内容？',
    answer: '您可以通过色影视首页的分类导航浏览电视剧、电影、综艺三大板块，也可以使用搜索功能精准查找您想看的影视剧集。首页还会推荐热门和最新内容，让您轻松发现好剧。',
  },
];

export default function Home() {
  const [activeCategory, setActiveCategory] = useState<string>('all');

  const filteredContent = activeCategory === 'all'
    ? allContent
    : allContent.filter((item) => item.category === activeCategory);

  const hotContent = allContent.filter((item) => item.isHot).slice(0, 6);
  const newContent = allContent.filter((item) => item.isNew).slice(0, 6);

  return (
    <>
      <ScrollToTop />
      <SEOSchema type="WebSite" data={{}} />
      <SEOSchema type="WebPage" data={{
        name: '色影视 | 电视剧全集在线与电影完整版不缓冲追剧 | 综艺全集播放连续观看',
        description: '电视剧全集在线观看、电影完整版播放、综艺全集免费看，不缓冲追剧连续播放，高清画质流畅体验，最新影视剧集大全一网打尽。',
        url: 'https://www.seyingtv.com/',
      }} />
      <SEOSchema type="FAQPage" data={{
        mainEntity: faqData.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Dark%20cinema%20theater%20interior%20with%20warm%20golden%20red%20ambient%20lighting%20elegant%20velvet%20curtains%20movie%20screen%20glowing%20atmospheric%20professional%20cinematic%20photography%20dramatic%20shadows%20rich%20textures%20luxurious%20vibe&width=1600&height=900&seq=hero-bg-2026&orientation=landscape"
              alt="色影视 - 在线影视平台"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-background-50"></div>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 w-full">
              <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-white max-w-3xl leading-tight">
                电视剧全集在线 · 电影完整版
                <br />
                <span className="text-accent-500">不缓冲追剧</span> 连续播放
              </h1>
              <p className="mt-4 md:mt-6 text-sm md:text-lg text-white/80 max-w-2xl">
                高清画质流畅体验，最新影视剧集大全一网打尽。综艺全集播放、电影大全、免费高清不缓冲，尽在色影视。
              </p>
              <div className="mt-8 flex flex-col sm:flex-row items-center gap-3">
                <Link
                  to="/category/dianshiju"
                  className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-full transition-all duration-300 text-sm whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-tv-line mr-2"></i>
                  开始追剧
                </Link>
                <Link
                  to="/category/dianying"
                  className="px-8 py-3 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white font-medium rounded-full border border-white/30 transition-all duration-300 text-sm whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-film-line mr-2"></i>
                  观看电影
                </Link>
              </div>
            </div>
          </section>

          {/* Category Navigation */}
          <section className="w-full max-w-[1280px] mx-auto px-4 md:px-6 -mt-10 relative z-10">
            <div className="bg-background-50 rounded-xl border border-background-200 p-4 md:p-6">
              <div className="flex items-center justify-center gap-2 flex-wrap">
                <button
                  onClick={() => setActiveCategory('all')}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                    activeCategory === 'all'
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  全部
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.slug}
                    onClick={() => setActiveCategory(cat.slug)}
                    className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                      activeCategory === cat.slug
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    <i className={`${cat.icon} mr-1.5`}></i>
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* Content Grid */}
          <section className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                {activeCategory === 'all' ? '精选推荐' : categories.find((c) => c.slug === activeCategory)?.name}
              </h2>
              {activeCategory !== 'all' && (
                <Link
                  to={`/category/${activeCategory}`}
                  className="text-sm text-primary-500 hover:text-primary-600 font-medium cursor-pointer whitespace-nowrap"
                >
                  查看全部 <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {filteredContent.slice(0, 6).map((item) => (
                <MovieCard key={item.id} item={item} />
              ))}
            </div>
          </section>

          {/* Hot Content */}
          <section className="w-full bg-background-100 py-10 md:py-14">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-8 h-8 flex items-center justify-center bg-primary-500 text-white rounded-lg">
                  <i className="ri-fire-line"></i>
                </span>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                  热门推荐
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {hotContent.map((item) => (
                  <MovieCard key={item.id} item={item} />
                ))}
              </div>
            </div>
          </section>

          {/* New Content */}
          <section className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-8 h-8 flex items-center justify-center bg-accent-500 text-white rounded-lg">
                <i className="ri-flashlight-line"></i>
              </span>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                最新上线
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {newContent.map((item) => (
                <MovieCard key={item.id} item={item} />
              ))}
            </div>
          </section>

          {/* Features */}
          <section className="w-full bg-background-100 py-10 md:py-14">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 text-center mb-10">
                为什么选择色影视
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="text-center p-6 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center bg-primary-100 rounded-full mb-4">
                    <i className="ri-hd-line text-2xl text-primary-500"></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">高清画质</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">支持1080P全高清和4K超高清播放，影院级观影体验</p>
                </div>
                <div className="text-center p-6 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center bg-accent-100 rounded-full mb-4">
                    <i className="ri-speed-up-line text-2xl text-accent-500"></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">不缓冲追剧</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">先进CDN加速技术，智能自适应码率，确保流畅不卡顿</p>
                </div>
                <div className="text-center p-6 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center bg-primary-100 rounded-full mb-4">
                    <i className="ri-timer-line text-2xl text-primary-500"></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">每日更新</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">最新影视剧集第一时间同步，永不错过精彩内容</p>
                </div>
                <div className="text-center p-6 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center bg-accent-100 rounded-full mb-4">
                    <i className="ri-device-line text-2xl text-accent-500"></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">多端观看</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">电脑、平板、手机全面适配，随时随地畅享影视</p>
                </div>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center py-8 px-4 rounded-xl bg-background-100">
                <div className="font-heading text-3xl md:text-4xl font-bold text-primary-500">10万+</div>
                <p className="mt-2 text-sm text-foreground-600">影视剧集资源</p>
              </div>
              <div className="text-center py-8 px-4 rounded-xl bg-background-100">
                <div className="font-heading text-3xl md:text-4xl font-bold text-accent-500">500万+</div>
                <p className="mt-2 text-sm text-foreground-600">日活跃用户</p>
              </div>
              <div className="text-center py-8 px-4 rounded-xl bg-background-100">
                <div className="font-heading text-3xl md:text-4xl font-bold text-primary-500">99.9%</div>
                <p className="mt-2 text-sm text-foreground-600">播放流畅率</p>
              </div>
              <div className="text-center py-8 px-4 rounded-xl bg-background-100">
                <div className="font-heading text-3xl md:text-4xl font-bold text-accent-500">4K</div>
                <p className="mt-2 text-sm text-foreground-600">超高清画质</p>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="w-full bg-background-100 py-10 md:py-14">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 text-center mb-10">
                常见问题
              </h2>
              <div className="max-w-3xl mx-auto space-y-3">
                {faqData.map((item, index) => (
                  <details key={index} className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden cursor-pointer">
                    <summary className="flex items-center justify-between px-5 py-4 text-sm md:text-base font-medium text-foreground-900 cursor-pointer">
                      {item.question}
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-300"></i>
                    </summary>
                    <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                      {item.answer}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="relative rounded-2xl overflow-hidden bg-cover bg-center p-10 md:p-16 text-center">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20cozy%20home%20living%20room%20with%20large%20TV%20screen%20glowing%20soft%20ambient%20light%20comfortable%20sofa%20evening%20atmosphere%20cinematic%20photography%20golden%20hour%20light%20lifestyle%20scene%20modern%20interior&width=1280&height=600&seq=cta-bg-2026&orientation=landscape"
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/50"></div>
              <div className="relative z-10">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
                  准备好开启追剧之旅了吗？
                </h2>
                <p className="text-white/80 text-sm md:text-base max-w-xl mx-auto mb-8">
                  海量电视剧全集、电影完整版、综艺全集播放，高清不缓冲，尽在色影视。
                </p>
                <Link
                  to="/category/dianshiju"
                  className="inline-flex items-center px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-full transition-all duration-300 text-sm whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-play-circle-line mr-2 text-lg"></i>
                  立即观看
                </Link>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}